// ... other imports remain unchanged
import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MobileHeader from './MobileHeader';
import PageHeader from './PageHeader';
import MainTableV2 from './MainTableV2';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const CardOrders = () => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const primaryColor = theme.primary;
  const apiURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const cardGroups = useSelector((state) => state.policies.cardGroups);
  const policies = useSelector((state) => state.policies.policies);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // New filter states
  const [fulfilledFilter, setFulfilledFilter] = useState('all'); // 'all', 'fulfilled', 'unfulfilled'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const clearAlert = () => setAlertMessage(null);

  // Fetch and sort orders by CreatedAt descending
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiURL}/dataServer?`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getCardOrders',
          payload: {
            userId: currentUser?.userId
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        const sortedOrders = result.sort(
          (a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt)
        );
        setData(sortedOrders);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.userId) {
      fetchData();
    }
  }, [currentUser]);

  const handleEditClick = (order) => {
    navigate('/editcardorder', { state: { orderId: order.OrderId } });
  };

  const columns = useMemo(() => [
    {
      name: 'Order ID',
      selector: (row) => row.OrderId || 'N/A',
      sortable: true,
      reorder: true,
      style: { width: '140px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Created',
      selector: (row) =>
        moment(row.CreatedAt)
          .tz('America/Chicago')
          .format('MM/DD/YYYY'),
      sortable: true,
      reorder: true,
      style: { width: '180px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Account',
      selector: (row) => {
        if (row.CardGroupId && cardGroups?.length) {
          const foundGroup = cardGroups.find(
            (g) => Number(g.cardGroupID) === Number(row.CardGroupId)
          );
          if (foundGroup) {
            return `${foundGroup.groupName} (${foundGroup.AccountNumber})`;
          }
        }
        if (row.PolicyNumber && policies?.length) {
          const foundPolicy = policies.find(
            (p) => Number(p.PolicyNumber) === Number(row.PolicyNumber)
          );
          if (foundPolicy) {
            return `${foundPolicy.CompanyName || foundPolicy.Description} (${foundPolicy.PolicyNumber})`;
          }
        }
        return 'N/A';
      },
      sortable: true,
      reorder: true,
      width: '250px'
    },
    {
      name: 'Address',
      selector: (row) => {
        const { AddressLine1, AddressLine2, City, State, Zip } = row;
        return [AddressLine1, AddressLine2, City, State, Zip].filter(Boolean).join(', ');
      },
      sortable: false,
      reorder: true,
      wrap: true
    },
    {
      name: 'Embossed Name',
      selector: (row) =>
        row.SubDomain ? row.groupName : (row.EmbossedNameOnCards || 'N/A'),
      sortable: true,
      reorder: true,
      wrap: true
    },
    {
      name: '# of Cards',
      selector: (row) => row.NumberOfCards || 0,
      sortable: true,
      reorder: true,
      style: { width: '120px', whiteSpace: 'nowrap' }
    },
    {
      name: 'Fulfilled',
      selector: (row) => (row.Fulfilled ? 'Yes' : 'No'),
      sortable: true,
      reorder: true,
      style: { width: '100px', whiteSpace: 'nowrap' }
    }
    // Actions column removed per current configuration
  ], [cardGroups, policies]);

  const filterConfig = [
    { key: 'OrderId', type: 'string' },
    { key: 'EmbossedNameOnCards', type: 'string' },
    { key: 'groupName', type: 'string' },
    { key: 'City', type: 'string' },
    { key: 'State', type: 'string' },
    { key: 'Zip', type: 'string' },
    { key: 'AddressLine1', type: 'string' },
    { key: 'CreatedAt', type: 'date' }
  ];

  // Apply additional filters: fulfillment and date range
  const filteredData = useMemo(() => {
    let filtered = data;
    if (fulfilledFilter !== 'all') {
      filtered = filtered.filter(order =>
        fulfilledFilter === 'fulfilled' ? order.Fulfilled : !order.Fulfilled
      );
    }
    if (startDate) {
      filtered = filtered.filter(order => new Date(order.CreatedAt) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter(order => new Date(order.CreatedAt) <= new Date(endDate));
    }
    return filtered;
  }, [data, fulfilledFilter, startDate, endDate]);

  // Calculate metrics based on filteredData
  const orderMetrics = useMemo(() => {
    const fulfilledOrders = filteredData.filter(order => order.Fulfilled);
    const openOrders = filteredData.filter(order => !order.Fulfilled);
    const totalFulfilledOrders = fulfilledOrders.length;
    const totalOpenOrders = openOrders.length;
    const totalCardsFulfilled = fulfilledOrders.reduce(
      (sum, order) => sum + (order.NumberOfCards || 0),
      0
    );
    const totalCardsOpen = openOrders.reduce(
      (sum, order) => sum + (order.NumberOfCards || 0),
      0
    );
    return { totalFulfilledOrders, totalOpenOrders, totalCardsFulfilled, totalCardsOpen };
  }, [filteredData]);

  const ordersChartData = useMemo(() => ({
    labels: ['Fulfilled', 'Open'],
    datasets: [
      {
        label: 'Number of Orders',
        data: [orderMetrics.totalFulfilledOrders, orderMetrics.totalOpenOrders],
        backgroundColor: [primaryColor, '#ccc']
      }
    ]
  }), [orderMetrics, primaryColor]);

  const cardsChartData = useMemo(() => ({
    labels: ['Fulfilled', 'Open'],
    datasets: [
      {
        label: 'Number of Cards',
        data: [orderMetrics.totalCardsFulfilled, orderMetrics.totalCardsOpen],
        backgroundColor: [primaryColor, '#ccc']
      }
    ]
  }), [orderMetrics, primaryColor]);

  return (
    <div className="mobile-container mx-auto px-4 sm:px-6 lg:px-8">
      <MobileHeader theme={theme} />
      <PageHeader
        heading="Card Orders"
        context="Review all card orders placed by users."
      />

      {/* Summary Metrics, Charts and Filters Section */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col items-center">
            <h2 className="text-lg font-semibold">Orders</h2>
            <div className="flex space-x-4 mt-2">
              <div className="bg-gray-100 rounded-lg p-4 shadow-sm flex flex-col items-center">
                <span className="text-md font-semibold">Fulfilled Orders</span>
                <span className="text-green-500 text-lg font-bold">
                  {orderMetrics.totalFulfilledOrders}
                </span>
              </div>
              <div className="bg-gray-100 rounded-lg p-4 shadow-sm flex flex-col items-center">
                <span className="text-md font-semibold">Open Orders</span>
                <span className="text-red-500 text-lg font-bold">
                  {orderMetrics.totalOpenOrders}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <h2 className="text-lg font-semibold">Cards</h2>
            <div className="flex space-x-4 mt-2">
              <div className="bg-gray-100 rounded-lg p-4 shadow-sm flex flex-col items-center">
                <span className="text-md font-semibold">Cards (Fulfilled)</span>
                <span className="text-green-500 text-lg font-bold">
                  {orderMetrics.totalCardsFulfilled}
                </span>
              </div>
              <div className="bg-gray-100 rounded-lg p-4 shadow-sm flex flex-col items-center">
                <span className="text-md font-semibold">Cards (Open)</span>
                <span className="text-red-500 text-lg font-bold">
                  {orderMetrics.totalCardsOpen}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-100 rounded-lg p-4 shadow-sm">
            <h3 className="text-center font-semibold mb-2">Orders Overview</h3>
            <Bar
              data={ordersChartData}
              options={{ responsive: true, plugins: { legend: { display: false } } }}
            />
          </div>
          <div className="bg-gray-100 rounded-lg p-4 shadow-sm">
            <h3 className="text-center font-semibold mb-2">Cards Overview</h3>
            <Bar
              data={cardsChartData}
              options={{ responsive: true, plugins: { legend: { display: false } } }}
            />
          </div>
        </div>

        {/* Filters Section */}
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Filters</h3>
          <div className="flex flex-col md:flex-row md:items-end md:space-x-4">
            <div className="w-full md:w-1/3">
              <label className="block text-sm font-medium text-gray-700">
                Fulfillment
              </label>
              <select
                value={fulfilledFilter}
                onChange={(e) => setFulfilledFilter(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              >
                <option value="all">All</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="unfulfilled">Unfulfilled</option>
              </select>
            </div>
            <div className="w-full md:w-1/3">
              <label className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div className="w-full md:w-1/3">
              <label className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <MainTableV2
        columns={columns}
        data={filteredData}
        primaryColor={primaryColor}
        loading={loading}
        alertMessage={alertMessage}
        clearAlert={clearAlert}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterConfig={filterConfig}
      >
        {/* Optional action button can go here */}
      </MainTableV2>
    </div>
  );
};

export default CardOrders;
